import { useEffect } from 'react';

const ENABLED = false; // String(import.meta.env.VITE_ENABLE_DEBUG_LOGGER || '').toLowerCase() === 'true';

const makeSessionId = () => {
  try {
    return crypto.randomUUID();
  } catch {
    return `dbg-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  }
};

const formatArgs = (args) => args.map((arg) => {
  if (typeof arg === 'string') {
    return arg;
  }
  try {
    return JSON.stringify(arg);
  } catch {
    return String(arg);
  }
}).join(' ');

const postLog = (payload) => {
  if (!ENABLED) return;
  fetch('/api/client-log', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    keepalive: true,
  }).catch(() => undefined);
};

export default function DebugLogger() {
  useEffect(() => {
    if (!ENABLED) {
      return undefined;
    }

    const session = makeSessionId();

    const wrapConsole = (method, level) => {
      const original = console[method];
      if (typeof original !== 'function') {
        return () => undefined;
      }
      const wrapped = (...args) => {
        original(...args);
        const message = formatArgs(args);
        postLog({ level, message, session, extra: { consoleMethod: method } });
      };
      console[method] = wrapped;
      return () => { console[method] = original; };
    };

    const cleanupFns = [
      wrapConsole('error', 'error'),
      wrapConsole('warn', 'warning'),
      wrapConsole('info', 'info'),
      wrapConsole('debug', 'debug'),
    ];

    const handleError = (event) => {
      const message = event.message || (event.error && event.error.stack) || 'Script error';
      postLog({
        level: 'error',
        message,
        session,
        extra: {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
        },
      });
    };

    const handleRejection = (event) => {
      const reason = event.reason instanceof Error ? event.reason.stack : formatArgs([event.reason]);
      postLog({
        level: 'error',
        message: 'Unhandled promise rejection',
        session,
        extra: { reason },
      });
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleRejection);

    return () => {
      cleanupFns.forEach((fn) => fn());
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleRejection);
    };
  }, []);

  return null;
}
